// FILE: src/components/DocumentsPanel.tsx
// PHOENIX PROTOCOL - DOCUMENTS PANEL V27.1 (OPTIMISTIC UPLOAD + IMMEDIATE PROGRESS)
// V27.1: OPTIMISTIC UPDATE — kur klikon upload, karta shfaqet MENJËHERË me status
//        'PROCESSING' dhe progress 5% (jo pritje 2-4s te HTTP). Pastaj zëvendësohet
//        me dokumentin real kur upload-i përfundon. Progress bar tani shfaqet
//        MENJËHERË pas klikimit. Fix: `|| 30` → `?? 5` (trajtim i saktë i 0).
// V27.0: Zëvendësuar rose-* → danger-start, amber-* → warning-start.
// V26.0: FORENSIC LAB SELECTION HARMONY

import React, { useState, useRef, useEffect } from 'react';
import { Document, ConnectionStatus, DeletedDocumentResponse } from '../data/types';
import { TFunction } from 'i18next';
import { apiService } from '../services/api';
import moment from 'moment';
import { 
    FolderOpen, Eye, Trash, Plus, Loader2, 
    Archive, Pencil, CheckSquare, Square, XCircle, 
    AlertTriangle, CheckCircle2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ArchiveImportModal from './ArchiveImportModal';
import { sanitizeDocument } from '../utils/documentUtils';

interface DocumentsPanelProps {
  caseId: string;
  documents: Document[];
  t: TFunction;
  onDocumentDeleted: (response: DeletedDocumentResponse) => void;
  onDocumentUploaded: (newDocument: Document) => void;
  onViewOriginal: (document: Document) => void;
  onRename?: (document: Document) => void; 
  onVerifyDocumentLaws?: (document: Document) => void;
  selectedDocId?: string;
  onSelectDocument?: (document: Document) => void;
  connectionStatus: ConnectionStatus;
  reconnect: () => void; 
  className?: string;
}

const DocumentsPanel: React.FC<DocumentsPanelProps> = ({
  caseId,
  documents,
  connectionStatus,
  onDocumentDeleted,
  onDocumentUploaded,
  onViewOriginal,
  onRename,
  selectedDocId,
  onSelectDocument,
  t,
  className
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [isUploading, setIsUploading] = useState(false);
  const [uploadNotice, setUploadNotice] = useState<{ text: string; type: 'error' | 'warning' } | null>(null);
  
  const [archivingId, setScanningIdArchive] = useState<string | null>(null); 
  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showArchiveImport, setShowArchiveImport] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // ═══════════════════════════════════════════════════════════════════════
  // V27.1: OPTIMISTIC UPLOAD STATE
  // Kartelat e perkohshme shfaqen menjëherë, para se HTTP POST te perfundoje.
  // Zëvendësohen me dokumentin real ose hiqen nëse upload-i dështon.
  // ═══════════════════════════════════════════════════════════════════════
  const [optimisticDocs, setOptimisticDocs] = useState<Document[]>([]);

  const isSystemBusy = isUploading;

  useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
          if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
              setShowAddMenu(false);
          }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const performUpload = async (file: File): Promise<void> => {
    if (file.name.startsWith('.')) return;
    setIsUploading(true);
    setUploadNotice(null);

    // ═══════════════════════════════════════════════════════════════════
    // V27.1: Optimistic — shto kartelë MENJËHERË me progress 5%
    // ═══════════════════════════════════════════════════════════════════
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const tempDoc: Document = {
      id: tempId,
      file_name: file.name,
      file_type: file.type || file.name.split('.').pop() || 'unknown',
      storage_key: '',
      created_at: new Date().toISOString(),
      status: 'PROCESSING',
      progress_percent: 5,
      progress_message: 'Duke u ngarkuar skedarin...',
    };
    setOptimisticDocs(prev => [tempDoc, ...prev]);

    try {
      const responseData = await apiService.uploadDocument(caseId, file, () => {});
      const rawData = responseData as any;
      const newDoc: Document = {
          ...responseData,
          id: responseData.id || rawData._id, 
          file_name: file.name,
          file_type: file.type || (file.name.split('.').pop() || 'unknown'),
          storage_key: (responseData as any).storage_key || '',
          status: 'PROCESSING',
          progress_percent: 30,
          progress_message: 'Duke përgatitur skedarin...',
          created_at: new Date().toISOString()
      } as any;

      // Hiq temp-in dhe shto dokumentin real (React baton të dyja)
      setOptimisticDocs(prev => prev.filter(d => d.id !== tempId));
      onDocumentUploaded(newDoc);
    } catch (error: any) {
      console.error(`Failed to upload ${file.name}`, error);
      // Hiq temp-in nëse upload dështoi
      setOptimisticDocs(prev => prev.filter(d => d.id !== tempId));
      const errorMsg = error?.response?.data?.detail || error?.message || `${t('documentsPanel.uploadFailed', 'Dështoi ngarkimi')}: ${file.name}`;
      setUploadNotice({ text: errorMsg, type: 'error' });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadNotice(null);

    const normalizedName = file.name.toLowerCase().trim();
    const isDuplicate = documents.some(
      (d) => (d.file_name || (d as any).title || '').toLowerCase().trim() === normalizedName
    );

    if (isDuplicate) {
      setUploadNotice({
        text: `Dokumenti "${file.name}" tashmë ekziston në këtë lëndë. Fshini versionin ekzistues nëse dëshironi ta zëvendësoni.`,
        type: 'warning',
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    await performUpload(file);
  };

  const handleDeleteDocument = async (documentId: string | undefined) => {
    if (!documentId) return;
    if (!window.confirm(t('documentsPanel.confirmDelete', 'A jeni i sigurt që doni të fshini këtë dokument?'))) return;
    try {
      const response = await apiService.deleteDocument(caseId, documentId);
      onDocumentDeleted(response);
    } catch (error) { 
      alert(t('documentsPanel.deleteFailed', 'Fshirja e dokumentit dështoi.')); 
    }
  };

  const handleArchiveDocument = async (docId: string) => {
      setScanningIdArchive(docId);
      try {
          await apiService.archiveCaseDocument(caseId, docId);
          alert(t('documentsPanel.archiveSuccess', 'Dokumenti u arkivua me sukses!'));
      } catch (error) { 
        alert(t('documentsPanel.archiveFailed', 'Arkivimi dështoi.')); 
      } finally { 
        setScanningIdArchive(null); 
      }
  };

  const toggleSelectAll = () => {
      if (bulkSelectedIds.size === displayDocs.length) {
          setBulkSelectedIds(new Set()); 
      } else {
          const allIds = displayDocs.map(d => d.id);
          setBulkSelectedIds(new Set(allIds));
      }
  };

  const toggleBulkSelect = (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setBulkSelectedIds(prev => {
          const newSet = new Set(prev);
          if (newSet.has(id)) newSet.delete(id);
          else newSet.add(id);
          return newSet;
      });
  };

  const handleBulkDelete = async () => {
      if (!window.confirm(`A jeni i sigurt që doni të fshini ${bulkSelectedIds.size} dokumente?`)) return;
      setIsBulkDeleting(true);
      try {
          const idsToDelete = Array.from(bulkSelectedIds).filter(id => !id.startsWith('temp_'));
          await apiService.bulkDeleteDocuments(caseId, idsToDelete);
          idsToDelete.forEach(id => {
              onDocumentDeleted({ documentId: id, deletedFindingIds: [] });
          });
          setBulkSelectedIds(new Set());
      } catch (error) {
          alert("Fshirja masive dështoi.");
      } finally {
          setIsBulkDeleting(false);
      }
  };

  const handleArchiveImportComplete = async (_count: number) => {
      try {
          const updatedDocuments = await apiService.getDocuments(caseId);
          const currentIds = new Set(documents.map(d => d.id));
          const newDocs = updatedDocuments.filter(d => !currentIds.has(d.id));
          newDocs.forEach(doc => {
              onDocumentUploaded(sanitizeDocument(doc));
          });
      } catch (error) {
          console.error("Failed to refresh documents after import", error);
      }
  };

  const statusDotColor = (status: ConnectionStatus) => {
    switch (status) {
      case 'CONNECTED': 
        return 'bg-success-start shadow-md shadow-success-start/50 animate-pulse';
      case 'CONNECTING': 
        return 'bg-warning-start animate-pulse';
      default: 
        return 'bg-danger-start animate-pulse';
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // V27.1: MERGE per display — optimistic + real
  // Optimistic docs shfaqen ne krye (newest first).
  // ═══════════════════════════════════════════════════════════════════════
  const displayDocs = [...optimisticDocs, ...documents];

  const isSelectionMode = bulkSelectedIds.size > 0;

  return (
    <>
    <div className={`glass-panel p-4 rounded-2xl flex flex-col h-full overflow-hidden bg-canvas ${className}`}>
      
      {/* Header Bar */}
      <div className={`flex flex-row justify-between items-center border-b pb-3 mb-4 flex-shrink-0 gap-2 transition-colors duration-300 ${
        isSelectionMode ? 'border-danger-start/30 bg-danger-start/10 -mx-4 px-4 py-2 mt-[-1rem] rounded-t-2xl' : 'border-main'
      }`}>
        
        {isSelectionMode ? (
            <div className="flex items-center justify-between w-full h-11">
                <div className="flex items-center gap-3">
                    <button 
                        onClick={() => setBulkSelectedIds(new Set())} 
                        className="flex items-center justify-center w-11 h-11 text-text-muted hover:text-text-primary transition-colors focus:outline-none cursor-pointer"
                        aria-label="Clear selection"
                    >
                        <XCircle size={20} />
                    </button>
                    <span className="text-danger-start font-bold text-sm">{bulkSelectedIds.size} të zgjedhura</span>
                </div>
                <button 
                    onClick={handleBulkDelete} 
                    disabled={isBulkDeleting}
                    className="flex items-center gap-2 px-4 h-11 bg-danger-start hover:bg-danger-start/90 text-white rounded-xl font-bold text-xs uppercase tracking-wider transition-colors shadow-lg shadow-danger-start/25 focus:outline-none cursor-pointer active:scale-95"
                >
                    {isBulkDeleting ? <Loader2 size={16} className="animate-spin" /> : <Trash size={15} />}
                    Fshi Të Gjitha
                </button>
            </div>
        ) : (
            <>
                <div className="flex items-center gap-2 min-w-0 h-11">
                    <button 
                        onClick={toggleSelectAll} 
                        className="flex items-center justify-center w-11 h-11 text-text-muted hover:text-text-primary transition-colors focus:outline-none cursor-pointer" 
                        title="Select All"
                    >
                        {displayDocs.length > 0 && bulkSelectedIds.size === displayDocs.length ? <CheckSquare size={18} className="text-primary-start" /> : <Square size={18} />}
                    </button>
                    <h2 className="text-base font-bold text-text-primary truncate select-none">{t('documentsPanel.title', 'Dokumentet')}</h2>
                    <div className="flex items-center justify-center ml-1">
                        <span className={`w-2 h-2 rounded-full ${statusDotColor(connectionStatus)} transition-all duration-300`} />
                    </div>
                </div>

                <div className="relative h-11 flex items-center" ref={dropdownRef}>
                    <motion.button 
                        onClick={() => !isSystemBusy && setShowAddMenu(!showAddMenu)}
                        disabled={isSystemBusy}
                        whileTap={{ scale: 0.95 }}
                        className={`h-11 w-11 flex items-center justify-center rounded-xl shadow-sm transition-all focus:outline-none cursor-pointer ${
                            isSystemBusy 
                                ? 'bg-surface text-text-disabled cursor-not-allowed border border-main' 
                                : 'btn-primary p-0'
                        }`}
                        title={isSystemBusy ? "Prisni që dokumenti të ngarkohet..." : "Shto Dokument"}
                    >
                        {isSystemBusy ? <Loader2 className="h-5 w-5 animate-spin text-text-muted" /> : <Plus className="h-5 w-5" />}
                    </motion.button>

                    <AnimatePresence>
                        {showAddMenu && !isSystemBusy && (
                            <motion.div 
                                initial={{ opacity: 0, y: 6, scale: 0.96 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: 6, scale: 0.96 }}
                                transition={{ duration: 0.12 }}
                                className="absolute right-0 top-12 w-52 rounded-2xl shadow-2xl border border-main z-50 overflow-hidden text-text-primary bg-card divide-y divide-main"
                            >
                                <button 
                                    onClick={() => { setShowAddMenu(false); fileInputRef.current?.click(); }} 
                                    className="w-full text-left px-4 py-3 hover:bg-hover text-xs font-bold uppercase tracking-wider text-text-primary transition-colors focus:outline-none cursor-pointer"
                                >
                                    Ngarko Dokument
                                </button>
                                <button 
                                    onClick={() => { setShowAddMenu(false); setShowArchiveImport(true); }} 
                                    className="w-full text-left px-4 py-3 hover:bg-hover text-xs font-bold uppercase tracking-wider text-text-primary transition-colors focus:outline-none cursor-pointer"
                                >
                                    Importo nga Arkiva
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                <input 
                  type="file" 
                  ref={fileInputRef} 
                  onChange={handleFileChange} 
                  className="hidden" 
                  disabled={isSystemBusy} 
                />
            </>
        )}
      </div>

      {uploadNotice && (
        <div
          className={`p-3 text-xs rounded-xl mb-4 font-medium flex items-start gap-2 border ${
            uploadNotice.type === 'warning'
              ? 'bg-warning-start/10 text-warning-start border-warning-start/30'
              : 'bg-danger-start/10 text-danger-start border-danger-start/30'
          }`}
        >
          <AlertTriangle size={15} className="shrink-0 mt-0.5" />
          <div className="flex-1">{uploadNotice.text}</div>
          <button
            type="button"
            onClick={() => setUploadNotice(null)}
            className="text-text-muted hover:text-text-primary text-xs font-bold"
          >
            ✕
          </button>
        </div>
      )}
      
      {/* Scrollable Container */}
      <div className="space-y-2 flex-1 overflow-y-auto overflow-x-hidden pr-1.5 custom-finance-scroll min-h-0 bg-canvas/20 rounded-xl p-2 border border-main">
        {displayDocs.length === 0 && (
          <div className="text-text-muted text-center py-12 flex flex-col items-center opacity-60">
            <FolderOpen className="w-12 h-12 mb-3 text-text-disabled/20" />
            <p className="text-sm font-medium">{t('documentsPanel.noDocuments', 'Nuk ka dokumente në këtë lëndë.')}</p>
          </div>
        )}
        
        {displayDocs.map((doc) => {
          const isProcessingState = doc.status === 'PENDING' || doc.status === 'PROCESSING';
          const isOptimistic = String(doc.id).startsWith('temp_');
          // V27.1: `?? 5` (jo `|| 30`) — nese progress=0, tregojme 5% per vizual
          const progressPercent = isProcessingState ? (doc.progress_percent ?? 5) : 100;

          const statusText = isProcessingState 
            ? (doc.progress_message || 'Duke procesuar...')
            : 'Gati';

          const isDocActive = String(doc.id) === String(selectedDocId);
          const isBulkSelected = bulkSelectedIds.has(doc.id);

          return (
            <motion.div 
                key={doc.id} 
                layout="position" 
                onClick={() => {
                  if (onSelectDocument && !isOptimistic) onSelectDocument(doc);
                }} 
                className={`group flex items-center justify-between p-3 border rounded-xl transition-all ${
                    isOptimistic ? 'opacity-90' : 'cursor-pointer'
                } ${
                    isDocActive 
                        ? 'bg-primary-start/10 border-primary-start text-primary-start shadow-sm' 
                        : 'bg-surface/30 hover:bg-hover border-main'
                }`}
                initial={{ opacity: 0, y: -6 }} 
                animate={{ opacity: isOptimistic ? 0.9 : 1, y: 0 }}
            >
              
              <div className="min-w-0 flex-1 pr-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={(e) => { if (!isOptimistic) toggleBulkSelect(doc.id, e); }}
                    disabled={isOptimistic}
                    className={`flex items-center justify-center w-7 h-7 shrink-0 transition-colors focus:outline-none ${
                      isOptimistic ? 'text-text-disabled cursor-not-allowed' : 'text-text-muted hover:text-primary-start cursor-pointer'
                    }`}
                    title={isOptimistic ? 'Duke u ngarkuar...' : (isBulkSelected ? 'Hiq zgjedhjen' : 'Zgjidh dokumentin')}
                    aria-label={isBulkSelected ? 'Hiq zgjedhjen' : 'Zgjidh dokumentin'}
                  >
                    {isBulkSelected ? <CheckSquare size={16} className="text-primary-start" /> : <Square size={16} />}
                  </button>
                  <p className="text-sm font-semibold truncate text-text-primary">{doc.file_name}</p>
                  {isDocActive && <CheckCircle2 size={15} className="text-primary-start shrink-0" />}
                </div>
                {isProcessingState ? (
                    <div className="flex items-center gap-3 mt-1.5">
                        <span className="text-[10px] text-primary-start font-bold uppercase tracking-wider truncate max-w-[140px] sm:max-w-[200px]">
                          {statusText}
                        </span>
                        <div className="w-24 sm:w-28 h-1.5 bg-surface rounded-full overflow-hidden border border-main">
                          <motion.div 
                            className="h-full bg-primary-start" 
                            animate={{ width: `${progressPercent}%` }} 
                            transition={{ ease: "linear", duration: 0.3 }} 
                          />
                        </div>
                        <span className="text-xs text-text-muted font-mono font-bold">{progressPercent}%</span>
                    </div>
                ) : (
                  <p className="text-xs text-text-muted truncate mt-0.5 font-medium font-mono">{moment(doc.created_at).format('DD MMM YYYY, HH:mm')}</p>
                )}
              </div>
              
              {/* Row action tools */}
              <div className={`flex items-center gap-1 flex-shrink-0 transition-opacity ${isSelectionMode || isOptimistic ? 'opacity-30 pointer-events-none' : 'opacity-60 group-hover:opacity-100'}`}>
                
                {!isProcessingState && !isOptimistic && (
                    <button 
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onRename && onRename(doc); }} 
                        className="flex items-center justify-center w-8 h-8 hover:bg-hover rounded-lg text-text-muted hover:text-text-primary transition-colors focus:outline-none cursor-pointer" 
                        title={t('documentsPanel.rename', 'Riemërto')}
                    >
                        <Pencil size={13} />
                    </button>
                )}
                
                {!isProcessingState && !isOptimistic && (
                    <button 
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onViewOriginal(doc); }} 
                        className="flex items-center justify-center w-8 h-8 hover:bg-hover rounded-lg text-primary-start transition-colors focus:outline-none cursor-pointer" 
                        title={t('documentsPanel.viewOriginal', 'Shiko')}
                    >
                        <Eye size={13} />
                    </button>
                )}
                
                {!isProcessingState && !isOptimistic && (
                    <button 
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleArchiveDocument(doc.id); }} 
                        className="flex items-center justify-center w-8 h-8 hover:bg-hover rounded-lg text-text-muted hover:text-text-primary transition-colors focus:outline-none cursor-pointer" 
                        title={t('documentsPanel.archive', 'Arkivo')}
                    >
                        {archivingId === doc.id ? <Loader2 size={13} className="animate-spin text-primary-start" /> : <Archive size={13} />}
                    </button>
                )}

                {/* DELETE BUTTON — nuk shfaqet per optimistic */}
                {!isOptimistic && (
                  <button 
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleDeleteDocument(doc.id); }} 
                      className="flex items-center justify-center w-8 h-8 hover:bg-danger-start/15 rounded-lg text-danger-start hover:text-danger-start transition-colors focus:outline-none cursor-pointer" 
                      title={t('documentsPanel.delete', 'Fshij')}
                  >
                      <Trash size={13} />
                  </button>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>

    {/* IMPORT MODAL */}
    <ArchiveImportModal 
        isOpen={showArchiveImport} 
        onClose={() => setShowArchiveImport(false)} 
        caseId={caseId}
        onImportComplete={handleArchiveImportComplete}
    />
    </>
  );
};

export default DocumentsPanel;